import { useRouter } from "next/router";
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useWebPushSubscription } from "src/hooks/useWebPushSubscription";
import { firstAppOpened, setAppOpenedAt } from "src/store/localStorage/firstAppOpened";
import { pagesPath } from "src/utils/$path";

type PWADeviceProviderProps = {
  isMobile: boolean;
  isPWA: boolean;
  isFirstMobilePWAVisit: boolean;
  fetchCurrentEndpointSubscribed: () => Promise<boolean>;
  currentEndpointSubscribed: boolean;
};

const context = createContext<PWADeviceProviderProps>({
  isMobile: false,
  isPWA: false,
  isFirstMobilePWAVisit: false,
  fetchCurrentEndpointSubscribed: async () => false,
  currentEndpointSubscribed: false,
});

const { Provider } = context;

export const PWADeviceProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const router = useRouter();

  const ready = useRef<boolean>(false);
  const initialFirstAppOpened = useRef<boolean>(false);

  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isPWA, setIsPWA] = useState<boolean>(false);
  const isMobilePWA = useMemo(() => isMobile && isPWA, [isMobile, isPWA]);

  const { fetchCurrentEndpointSubscribed: fetch } = useWebPushSubscription();

  const [currentEndpointSubscribed, setCurrentEndpointSubscribed] = useState<boolean>(false);

  useEffect(() => {
    if (typeof localStorage === "undefined") return;

    if (!ready.current) {
      initialFirstAppOpened.current = firstAppOpened({ storage: localStorage });
      !firstAppOpened({ storage: localStorage }) && setAppOpenedAt({ storage: localStorage });
    }

    ready.current = true;

    isMobilePWA &&
      !initialFirstAppOpened.current &&
      !currentEndpointSubscribed &&
      router.pathname !== pagesPath.home.notification_setting.$url().pathname &&
      router.push(pagesPath.home.notification_setting.$url().pathname);

    // NOTE: パスの変更ではレンダリングさせないため、`router` は監視対象外とする
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobilePWA, currentEndpointSubscribed]);

  const fetchCurrentEndpointSubscribed = useCallback(async (): Promise<boolean> => {
    if (!ready.current) return false;

    const subscribed = await fetch();

    setCurrentEndpointSubscribed(subscribed);

    return subscribed;
  }, [ready, fetch, setCurrentEndpointSubscribed]);

  useEffect(() => {
    // NOTE: https://developer.mozilla.org/ja/docs/Web/HTTP/Browser_detection_using_the_user_agent#mobile_device_detection
    //       タッチスクリーンがあるデバイスは Surface など色々あるが、推奨環境は iPad であり、iPad かそうでないかを判定できれば良い
    setIsMobile("maxTouchPoints" in navigator && navigator.maxTouchPoints > 0);

    // NOTE: https://www.ykicchan.dev/posts/2021-03-26
    setIsPWA(window.matchMedia("(display-mode: standalone)").matches);

    const serviceWorker = navigator.serviceWorker;

    if (!ready.current) return;
    if (isMobile && !isPWA) return;

    // NOTE: iPad ブラウザでは `ServiceWorker` が undifined となるため、この条件は必要
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!serviceWorker) return;

    fetchCurrentEndpointSubscribed();
  }, [fetchCurrentEndpointSubscribed, isMobile, isPWA, ready]);

  return (
    <Provider
      value={{
        isMobile,
        isPWA,
        isFirstMobilePWAVisit: isMobilePWA && !initialFirstAppOpened.current,
        fetchCurrentEndpointSubscribed,
        currentEndpointSubscribed,
      }}
    >
      {children}
    </Provider>
  );
};

export const usePWADevice = (): PWADeviceProviderProps => {
  const {
    isMobile,
    isPWA,
    isFirstMobilePWAVisit,
    fetchCurrentEndpointSubscribed,
    currentEndpointSubscribed,
  } = useContext(context);

  return {
    isMobile,
    isPWA,
    isFirstMobilePWAVisit,
    fetchCurrentEndpointSubscribed,
    currentEndpointSubscribed,
  };
};
